import { emptyValueForSchema, getSchemaAt, CASE_TYPE_SCHEMA } from "../../../../case-spec";
import { Badge } from "@/components/ui/badge";
import {
  ArrayShell,
  BoolField,
  MediaUrlField,
  PeopleSelect,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  bool,
  isRecord,
  peopleNames,
  renamePersonKeepCast,
  setCastRole,
  setPersonRole,
  str,
  type CastRole,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

const PATH = "canon.people";

export function PeoplePanel({ root, disabled, onChangeRoot }: PanelProps) {
  const people = asArray(getAtPath(root, PATH));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(people.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, "canon.people[0]");

  return (
    <ArrayShell
      title="人物"
      count={people.length}
      disabled={disabled}
      onAdd={() => {
        const row = itemSchema
          ? emptyValueForSchema(itemSchema)
          : {
              name: "",
              contact: {},
              is_protagonist: false,
              is_guide: false,
              is_player: false,
            };
        onChangeRoot(appendAtPath(root, PATH, row));
        setOpenIndex(people.length);
      }}
    >
      <ul className="flex flex-col gap-2">
        {people.map((person, index) => {
          const rec = isRecord(person) ? person : {};
          const name = str(rec.name) || `人物 ${index + 1}`;
          const itemPath = childPath(PATH, index);
          const roles: string[] = [];
          if (bool(rec.is_protagonist)) roles.push("主角");
          if (bool(rec.is_guide)) roles.push("向导");
          if (bool(rec.is_player)) roles.push("玩家");

          return (
            <RowCard
              key={itemPath}
              title={
                roles.length
                  ? `${name} · ${roles.join(" / ")}`
                  : name
              }
              open={openIndex === index}
              onToggle={() => toggle(index)}
              disabled={disabled}
              index={index}
              total={people.length}
              arrayPath={PATH}
              root={root}
              onChangeRoot={onChangeRoot}
            >
              <div className="flex flex-wrap gap-1">
                {roles.map((r) => (
                  <Badge key={r} variant="secondary">
                    {r}
                  </Badge>
                ))}
              </div>
              <TextField
                label="姓名"
                fieldKey="name"
                value={str(rec.name)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(renamePersonKeepCast(root, index, v))
                }
              />
              <TextField
                label="电话"
                value={str(getAtPath(root, childPath(itemPath, "contact.phone")))}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "contact.phone"), v),
                  )
                }
              />
              <TextField
                label="邮箱"
                value={str(getAtPath(root, childPath(itemPath, "contact.email")))}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "contact.email"), v),
                  )
                }
              />
              <TextField
                label="地址"
                value={str(
                  getAtPath(root, childPath(itemPath, "contact.address")),
                )}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "contact.address"), v),
                  )
                }
              />
              <MediaUrlField
                label="头像 URL"
                value={str(rec.avatar_url)}
                disabled={disabled}
                fieldKey="avatar_url"
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "avatar_url"), v),
                  )
                }
              />
              {(
                [
                  ["is_protagonist", "主角（机主）", "protagonist"],
                  ["is_guide", "向导", "guide"],
                  ["is_player", "玩家角色", "player"],
                ] as const
              ).map(([flag, label, role]) => (
                <BoolField
                  key={flag}
                  label={label}
                  value={bool(rec[flag])}
                  disabled={disabled}
                  onChange={(v) =>
                    onChangeRoot(setPersonRole(root, index, role as CastRole, v))
                  }
                />
              ))}
            </RowCard>
          );
        })}
      </ul>
    </ArrayShell>
  );
}

export function CastPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const names = peopleNames(root);
  const cast = {
    protagonist: str(getAtPath(root, "game.cast.protagonist")),
    guide: str(getAtPath(root, "game.cast.guide")),
    player: str(getAtPath(root, "game.cast.player")),
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        选择会同步到人物上的角色标记（is_protagonist / is_guide / is_player）。
        名单来自「人物」。
      </p>
      {(
        [
          ["protagonist", "主角（机主）"],
          ["guide", "向导"],
          ["player", "玩家角色"],
        ] as const
      ).map(([role, label]) => (
        <PeopleSelect
          key={role}
          label={label}
          value={cast[role]}
          names={names}
          disabled={disabled}
          allowEmpty
          onChange={(v) => onChangeRoot(setCastRole(root, role, v))}
        />
      ))}
      {names.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          尚无人物。请先在「人物」里添加。
        </p>
      ) : null}
    </div>
  );
}
